/**
 * Week ring component — web variant (ISSUE-068 / ADR-0020 §1).
 *
 * 7 plain dots labeled M T W T F S S, filled (leaf) when the user logged ≥1
 * resolved entry that day. Today subtly emphasized.
 *
 * NOTE: filled-dot deep-link to day view is deferred — the web app has no
 * /day/:date route yet. Dots are non-interactive on web for v1.
 *
 * D-07: binary, additive-only, never names gaps. No progress bars.
 * Animation: CSS transition on the fill, respects prefers-reduced-motion.
 */
const DAY_LABELS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'] as const;

function dayIso(weekStartDate: string, idx: number): string {
  const d = new Date(`${weekStartDate}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + idx);
  return d.toISOString().slice(0, 10);
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

interface WeekRingProps {
  weekStartDate: string;
  days: boolean[];
}

export function WeekRing({ weekStartDate, days }: WeekRingProps) {
  const today = todayIso();
  const filledCount = days.filter(Boolean).length;

  return (
    <div
      role="img"
      aria-label={`Logged ${filledCount} of 7 days this week`}
      className="flex items-end justify-between gap-1"
    >
      {days.map((filled, i) => {
        const date = dayIso(weekStartDate, i);
        const isToday = date === today;

        const dot = (
          <span
            aria-hidden
            className={[
              'block h-7 w-7 rounded-full transition-colors motion-reduce:transition-none',
              filled
                ? 'bg-leaf'
                : isToday
                  ? 'border-2 border-ink/40 bg-transparent'
                  : 'border border-husk bg-transparent',
            ].join(' ')}
          />
        );

        return (
          <div key={date} className="flex flex-1 flex-col items-center gap-1">
            {dot}
            <span
              aria-hidden
              className={`text-[10px] leading-none ${
                isToday ? 'font-semibold text-ink' : 'text-ink-soft'
              }`}
            >
              {DAY_LABELS[i]}
            </span>
          </div>
        );
      })}
    </div>
  );
}
